import { type ReactNode, useState } from "react";

export const PENDING_REVIEW_COMPARE_SKIP = new Set([
  "assignedSeller",
  "createdByAdmin",
  "updatedByAdmin",
  "categoryRelation",
  "hasPendingUpdate",
  "pendingUpdateSnapshot",
  "pendingUpdateSubmittedAt",
  "sellerUpdateReviewStatus",
  "sellerUpdateRejectionReason",
]);

/** Fields that should show thumbnails in the review dialog instead of raw URLs */
const IMAGE_FIELD_KEYS = new Set([
  "images",
  "thumbnailImage",
  "metaImage",
]);

const TIERED_PRICING_FIELD_KEY = "tieredPricing";

type TieredPricingRow = { quantity: number; price: number };

function parseTieredPricingValue(raw: unknown): TieredPricingRow[] {
  if (raw == null || raw === "") return [];

  let data: unknown = raw;
  if (typeof raw === "string") {
    try {
      data = JSON.parse(raw);
      if (typeof data === "string") {
        data = JSON.parse(data);
      }
    } catch {
      return [];
    }
  }

  const rows: TieredPricingRow[] = [];

  const tryPushTier = (quantityRaw: unknown, priceRaw: unknown) => {
    const quantity = Number(quantityRaw);
    const price = Number(priceRaw);
    if (Number.isFinite(quantity) && Number.isFinite(price)) {
      rows.push({ quantity, price });
      return true;
    }
    return false;
  };

  if (!Array.isArray(data) && data && typeof data === "object") {
    const asRecord = data as Record<string, unknown>;
    // Support object-map format: { "10": "99.5", "20": "95" }.
    for (const [qtyKey, priceValue] of Object.entries(asRecord)) {
      if (priceValue == null) continue;
      if (typeof priceValue !== "object" || Array.isArray(priceValue)) {
        tryPushTier(qtyKey, priceValue);
      }
    }
  }

  const list: unknown[] = Array.isArray(data)
    ? data
    : data && typeof data === "object"
      ? Object.values(data as Record<string, unknown>)
      : [];

  for (const item of list) {
    if (Array.isArray(item)) {
      // Accept tuple-style rows like [quantity, price].
      tryPushTier(item[0], item[1]);
      continue;
    }

    if (!item || typeof item !== "object") continue;
    const row = item as Record<string, unknown>;
    tryPushTier(
      row.quantity ?? row.unit ?? row.qty ?? row.minQuantity,
      row.price ?? row.unitPrice
    );
  }

  rows.sort((a, b) => a.quantity - b.quantity || a.price - b.price);
  return rows;
}

function normalizeForCompare(fieldKey: string, value: unknown): unknown {
  if (fieldKey === TIERED_PRICING_FIELD_KEY) {
    return parseTieredPricingValue(value);
  }
  return value;
}

function formatCompareValue(val: unknown): string {
  if (val === undefined || val === null) return "—";
  if (typeof val === "object") {
    try {
      return JSON.stringify(val);
    } catch {
      return String(val);
    }
  }
  return String(val);
}

function coerceImageUrlList(val: unknown): string[] {
  if (val == null) return [];
  if (typeof val === "string") {
    const t = val.trim();
    return t ? [t] : [];
  }
  if (Array.isArray(val)) {
    return val
      .filter((x): x is string => typeof x === "string" && x.trim().length > 0)
      .map((x) => x.trim());
  }
  return [];
}

function ReviewImageThumb({ url }: { url: string }) {
  const [broken, setBroken] = useState(false);

  if (broken) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs text-blue-600 underline break-all max-w-[160px] inline-block align-top"
      >
        Open link
      </a>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-block rounded-md border border-border overflow-hidden bg-muted/30 shrink-0 align-top"
      title={url}
    >
      <img
        src={url}
        alt=""
        className="h-20 w-20 object-cover block"
        loading="lazy"
        onError={() => setBroken(true)}
      />
    </a>
  );
}

function renderCompareCell(fieldKey: string, val: unknown): ReactNode {
  if (fieldKey === TIERED_PRICING_FIELD_KEY) {
    const rows = parseTieredPricingValue(val);
    if (rows.length === 0) {
      return <span className="text-muted-foreground">[]</span>;
    }
    return (
      <span className="break-words whitespace-pre-wrap">
        {JSON.stringify(rows)}
      </span>
    );
  }

  if (IMAGE_FIELD_KEYS.has(fieldKey)) {
    const urls = coerceImageUrlList(val);
    if (urls.length === 0) {
      return <span className="text-muted-foreground">—</span>;
    }
    return (
      <div className="flex flex-wrap gap-2 items-start">
        {urls.map((url, i) => (
          <ReviewImageThumb key={`${fieldKey}-${i}-${url.slice(0, 48)}`} url={url} />
        ))}
      </div>
    );
  }

  return (
    <span className="break-words whitespace-pre-wrap">
      {formatCompareValue(val)}
    </span>
  );
}

export type PendingReviewDiffRow = {
  key: string;
  currentCell: ReactNode;
  proposedCell: ReactNode;
};

export function buildPendingReviewDiffRows(
  current: Record<string, unknown>,
  proposed: Record<string, unknown>
): PendingReviewDiffRow[] {
  const keys = new Set([
    ...Object.keys(current),
    ...Object.keys(proposed),
  ]);
  const rows: PendingReviewDiffRow[] = [];

  for (const key of keys) {
    if (PENDING_REVIEW_COMPARE_SKIP.has(key)) continue;
    const a = normalizeForCompare(key, current[key]);
    const b = normalizeForCompare(key, proposed[key]);
    if (JSON.stringify(a) === JSON.stringify(b)) continue;
    rows.push({
      key,
      currentCell: renderCompareCell(key, a),
      proposedCell: renderCompareCell(key, b),
    });
  }

  rows.sort((x, y) => x.key.localeCompare(y.key));
  return rows;
}

/**
 * Shallow merge of pending snapshot onto a live product record (matches server preview, minus async category resolve).
 */
export function mergeSnapshotOntoLiveRecord(
  live: Record<string, unknown>,
  snapshot: Record<string, unknown> | null | undefined
): Record<string, unknown> {
  if (!snapshot || typeof snapshot !== "object" || Array.isArray(snapshot)) {
    return { ...live };
  }
  return { ...live, ...snapshot };
}

/**
 * Same idea as the Review changes table: only fields where merged snapshot differs from current listing.
 */
export function buildSellerUpdateHistoryDiffRows(
  live: Record<string, unknown>,
  submittedSnapshot: Record<string, unknown> | null | undefined
): PendingReviewDiffRow[] {
  const proposed = mergeSnapshotOntoLiveRecord(live, submittedSnapshot);
  return buildPendingReviewDiffRows(live, proposed);
}

export type SubmittedSnapshotDisplayRow = {
  key: string;
  valueCell: ReactNode;
};

/** Keys present in the submission patch only (not the whole product). */
export function buildSubmittedSnapshotDisplayRows(
  snapshot: Record<string, unknown> | null | undefined
): SubmittedSnapshotDisplayRow[] {
  if (!snapshot || typeof snapshot !== "object" || Array.isArray(snapshot)) {
    return [];
  }
  const rows: SubmittedSnapshotDisplayRow[] = [];
  for (const key of Object.keys(snapshot)) {
    if (PENDING_REVIEW_COMPARE_SKIP.has(key)) continue;
    rows.push({
      key,
      valueCell: renderCompareCell(key, snapshot[key]),
    });
  }
  rows.sort((a, b) => a.key.localeCompare(b.key));
  return rows;
}
